import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mantine/core';
import { clsx } from 'clsx';
import { useIntl } from 'react-intl';

import { wizardStepMessages } from './wizard-step-messages';
import styles from './WizardStep.module.css';

import type { WizardStepProps } from '../../freight-booking-wizard-types';

export const WizardStep = ({
  stepNumber,
  title,
  isActive,
  isCompleted,
  isWarning,
  onEdit,
  onCollapse,
  summary,
  children,
}: WizardStepProps) => {
  const intl = useIntl();

  // Warning state (highest priority): amber border, warning icon, subtitle, no body
  if (isWarning) {
    return (
      <div className={clsx(styles.step, styles.stepWarning)} data-testid={`wizard-step-${stepNumber}`}>
        <button className={styles.header} onClick={onEdit} type='button'>
          <div className={styles.circleContainer}>
            <FontAwesomeIcon className={styles.warningIcon} icon={faTriangleExclamation} />
          </div>
          <div className={styles.titleGroup}>
            <span className={styles.title}>{title}</span>
            <span className={styles.warningSubtitle}>{intl.formatMessage(wizardStepMessages.requiredFieldsMissing)}</span>
          </div>
          <FontAwesomeIcon className={styles.chevron} icon={faChevronDown} />
        </button>
      </div>
    );
  }

  // Completed + collapsed: summary card with an indigo check_circle icon and a ghost Edit button.
  if (isCompleted && !isActive) {
    return (
      <div className={clsx(styles.step, styles.stepCompleted)} data-testid={`wizard-step-${stepNumber}`}>
        <div className={styles.header}>
          <div className={styles.circleContainer}>
            <FontAwesomeIcon
              aria-label={intl.formatMessage(wizardStepMessages.completed)}
              className={styles.completedIcon}
              icon={faCircleCheck}
            />
          </div>
          <span className={styles.title}>{title}</span>
          <Button onClick={onEdit} size='compact-sm' type='button' variant='ghost'>
            {intl.formatMessage(wizardStepMessages.edit)}
          </Button>
        </div>
        {summary && <div className={styles.summary}>{summary}</div>}
      </div>
    );
  }

  // Pending + collapsed: numbered outlined circle
  if (!isActive) {
    return (
      <div className={styles.step} data-testid={`wizard-step-${stepNumber}`}>
        <button className={styles.header} onClick={onEdit} type='button'>
          <div className={styles.circleContainer}>
            <span className={clsx(styles.circle, styles.circlePending)}>{stepNumber}</span>
          </div>
          <span className={styles.title}>{title}</span>
          <FontAwesomeIcon className={styles.chevron} icon={faChevronDown} />
        </button>
      </div>
    );
  }

  // Expanded (active): box-shadow card, indigo outlined circle
  return (
    <div className={clsx(styles.step, styles.stepActive)} data-testid={`wizard-step-${stepNumber}`}>
      <div className={styles.header} onClick={onCollapse} role='button' tabIndex={0}>
        <div className={styles.circleContainer}>
          <span className={clsx(styles.circle, styles.circleActive)}>{stepNumber}</span>
        </div>
        <span className={styles.title}>{title}</span>
        <FontAwesomeIcon className={styles.chevron} icon={faChevronUp} />
      </div>
      <div className={styles.body}>{children}</div>
    </div>
  );
};
